import { environment } from '../../../environments/environment'
import { supabase, supabaseConfigurado } from '../supabase/cliente'
import { crearSubidorDeImagenes } from './subidor-de-imagenes'
import { SubidorSupabase } from './subidor-supabase'
import { backendDeUrl } from './rutas-storage'
import { normalizarImagenAJpeg } from '../util/normalizar-imagen'

/**
 * Adjuntar/reemplazar/eliminar fotos desde el DASHBOARD (pedido de Angie,
 * 2026-09-16, recuperar fotos perdidas de proveedores como LUCKY) — separado
 * a propósito de `sincronizador.ts` (mobile): el dashboard siempre tiene
 * internet real, así que escribe directo a Supabase sin pasar por la cola
 * offline de Dexie. Cero cambios al flujo mobile.
 *
 * Diferencia clave con mobile: mobile sube cada foto a una ruta FIJA (sin
 * timestamp) para nunca duplicar espacio al reeditar (ver rutas-storage.ts).
 * Acá, en cambio, cada reemplazo sube a una ruta NUEVA versionada — es lo que
 * permite ofrecer "deshacer" (la foto vieja sigue intacta en el storage,
 * nunca se pisa). El costo es un archivo huérfano por reemplazo no deshecho;
 * se acota a UN solo nivel: `fotos_reemplazo_anterior` guarda como mucho una
 * fila por campo (upsert), así que el reemplazo siguiente descarta el rastro
 * del huérfano más viejo sin dejar cadenas de historial creciendo sin fin.
 *
 * S3 nunca borra físicamente (ver comentario de `_supabaseParaBorrar` en
 * sincronizador.ts — el rol de la Lambda no tiene `s3:DeleteObject` y el CORS
 * del bucket no permite DELETE) — por eso ni "eliminar" ni la limpieza de un
 * "anterior" descartado intentan un borrado real contra S3, solo contra
 * Supabase Storage (fotos legado). Mismo costo aceptado que ya usa el resto
 * de la app: la ausencia de referencia ES el estado.
 */

const TABLA_ANTERIOR = 'fotos_reemplazo_anterior'

export type TablaConFoto = 'proveedores' | 'productos' | 'telas'

/** Describe a qué campo de foto se está apuntando, sin acoplar este módulo a los componentes de UI. */
export interface SlotFotoDashboard {
  tabla: TablaConFoto
  /** Nombre de la columna que identifica la fila: 'codigo' en proveedores, 'id' en productos/telas. */
  columnaId: 'codigo' | 'id'
  filaId: string
  /** Columna real en Postgres donde vive la URL, ej. 'foto_tarjeta_url'. */
  columna: string
  /** Prefijo de ruta en el storage, sin extensión, ej. `${codigo}/tarjeta`. */
  rutaBase: string
}

function exigirSupabaseConfigurado(): void {
  if (!supabaseConfigurado) {
    throw new Error('Supabase no está configurado en este ambiente — no se puede subir/reemplazar fotos desde el dashboard.')
  }
}

async function leerValorActual(slot: SlotFotoDashboard): Promise<string | null> {
  const { data, error } = await supabase
    .from(slot.tabla)
    .select(slot.columna)
    .eq(slot.columnaId, slot.filaId)
    .single()
  if (error) throw error
  const valor = (data as unknown as Record<string, unknown> | null)?.[slot.columna]
  return typeof valor === 'string' && valor.length > 0 ? valor : null
}

async function actualizarColumna(slot: SlotFotoDashboard, valor: string | null): Promise<void> {
  const { error } = await supabase.from(slot.tabla).update({ [slot.columna]: valor }).eq(slot.columnaId, slot.filaId)
  if (error) throw error
}

/** Registra (upsert) el "anterior" de este campo — como mucho una fila por (tabla, fila, campo), a propósito. */
async function registrarAnterior(slot: SlotFotoDashboard, urlAnterior: string): Promise<void> {
  const { error } = await supabase
    .from(TABLA_ANTERIOR)
    .upsert(
      { tabla: slot.tabla, fila_id: slot.filaId, campo: slot.columna, url_anterior: urlAnterior, creado_en: new Date().toISOString() },
      { onConflict: 'tabla,fila_id,campo' },
    )
  if (error) throw error
}

async function leerAnterior(slot: SlotFotoDashboard): Promise<string | null> {
  const { data, error } = await supabase
    .from(TABLA_ANTERIOR)
    .select('url_anterior')
    .eq('tabla', slot.tabla)
    .eq('fila_id', slot.filaId)
    .eq('campo', slot.columna)
    .maybeSingle()
  if (error) throw error
  return data?.url_anterior ?? null
}

async function borrarRegistroAnterior(slot: SlotFotoDashboard): Promise<void> {
  const { error } = await supabase
    .from(TABLA_ANTERIOR)
    .delete()
    .eq('tabla', slot.tabla)
    .eq('fila_id', slot.filaId)
    .eq('campo', slot.columna)
  if (error) throw error
}

/** ¿Hay una foto "anterior" pendiente de restaurar para este campo? Determina si el botón "Quitar" debe mostrarse. */
export async function hayAnteriorPendiente(slot: SlotFotoDashboard): Promise<boolean> {
  exigirSupabaseConfigurado()
  return (await leerAnterior(slot)) !== null
}

/**
 * Sube (o reemplaza) la foto de un campo. Orden crítico: primero se lee y
 * guarda el valor actual como "anterior" (si existe), y solo DESPUÉS se
 * actualiza la columna real — si algo falla a mitad de camino, nunca se
 * pierde la trazabilidad de a qué volver.
 */
export async function subirOReemplazarFoto(slot: SlotFotoDashboard, archivo: File): Promise<string> {
  exigirSupabaseConfigurado()
  const imagenJpeg = await normalizarImagenAJpeg(archivo)

  const valorActual = await leerValorActual(slot)
  if (valorActual) await registrarAnterior(slot, valorActual)

  const rutaVersionada = `${slot.rutaBase}-dash-${Date.now()}.jpg`
  const datos = await imagenJpeg.arrayBuffer()
  const subidor = crearSubidorDeImagenes(environment)
  const urlNueva = await subidor.subir(rutaVersionada, datos, 'image/jpeg')

  await actualizarColumna(slot, urlNueva)
  return urlNueva
}

/** Borra físicamente si es una foto legado de Supabase Storage; si es S3, no hace nada (ver comentario de archivo). */
async function intentarBorradoFisicoSiAplica(url: string | null): Promise<void> {
  if (!url) return
  const resuelto = backendDeUrl(url)
  if (!resuelto || resuelto.backend !== 'supabase') return
  try {
    await new SubidorSupabase().borrar([resuelto.ruta])
  } catch (err) {
    // Best-effort: si el borrado físico falla, igual seguimos — lo que
    // importa de verdad es que Postgres deje de referenciarla.
    console.warn('[reemplazo-fotos-dashboard] no se pudo borrar físicamente de Supabase Storage:', err)
  }
}

/**
 * "Quitar" (✕) — deshace el último reemplazo. `accion: 'restaurar'` vuelve a
 * la foto anterior; `accion: 'vaciar'` deja el campo vacío. En ambos casos se
 * descarta el registro de "anterior" (ya se resolvió, no queda nada pendiente).
 */
export async function deshacerReemplazo(slot: SlotFotoDashboard, accion: 'restaurar' | 'vaciar'): Promise<string | null> {
  exigirSupabaseConfigurado()
  const anterior = await leerAnterior(slot)

  if (accion === 'restaurar') {
    await actualizarColumna(slot, anterior)
    await borrarRegistroAnterior(slot)
    return anterior
  }

  // 'vaciar': el reemplazo actual queda huérfano en el storage (mismo costo
  // aceptado que el resto de la app) y el campo queda vacío.
  await actualizarColumna(slot, null)
  await borrarRegistroAnterior(slot)
  return null
}

/** "🗑 Eliminar" — borrado definitivo, sin posibilidad de deshacer. */
export async function eliminarFoto(slot: SlotFotoDashboard, urlActual: string | null): Promise<void> {
  exigirSupabaseConfigurado()
  await intentarBorradoFisicoSiAplica(urlActual)
  await actualizarColumna(slot, null)
  await borrarRegistroAnterior(slot)
}
